import {
  Autocomplete,
  Checkbox,
  Chip,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoPopover from "../../../Components/Common/poper/InfoPopover";
import ButtonComponent from "../../../Components/Common/button/Button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ApiService from "../../../utils/fetch";
import DataTable from "../../../Components/Common/datatable/DataTable";
import SaveIcon from "@mui/icons-material/Save";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { safeTimestamp } from "../../../Components/Common/DateFormatter";
import { useSelector } from "react-redux";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";
import RefreshIcon from "@mui/icons-material/Refresh";

function FGTargetDaysofSupply() {
  const [loading, setLoading] = useState(false);
  const [downloadloading, setDownloadLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const GridRef = useRef(null);
  const fileInputRef = useRef(null);

  const [filteredData, setFilteredData] = useState([]);
  const [bottlerList, setBottlerList] = useState([]);
  const [bottlerOptions, setBottlerOptions] = useState([]);
  const [userBottler, setUserBottler] = useState(null);
  const [selectedBottlerOwnerCodes, setSelectedBottlerOwnerCodes] = useState(
    [],
  );
  const roles = useSelector((state) => state.user.roles);

  useEffect(() => {
    fetchBottlerOwnerCode();
  }, []);

  const isAdminOrSuperUser = roles.some((role) =>
    ["ADMIN", "SUPER USER", "CSR", "DEMANDPLANNER"].includes(
      typeof role === "string" ? role.toUpperCase().trim() : "",
    ),
  );

  const fetchBottlerOwnerCode = async () => {
    try {
      const response = await ApiService.get(
        `/v1/fg-inventory/targetDays/bottlerOwnerCode`,
      );

      if (response.status === 200 && Array.isArray(response.data)) {
        const mappedBottlers = response.data.map((item) => ({
          id: item.bottlerOwnerCode,
          label: `${item.bottler} - ${item.bottlerOwnerCode}`,
          bottlerOwnerCode: item.bottlerOwnerCode,
          bottlerOwnerDescription: item.bottlerOwnerDescription || item.bottler,
        }));

        if (isAdminOrSuperUser) {
          setBottlerOptions(mappedBottlers);
          setUserBottler(null);
        } else {
          if (mappedBottlers.length > 0) {
            const singleBottler = mappedBottlers[0];
            setUserBottler(singleBottler);
            setSelectedBottlerOwnerCodes([singleBottler.id]);
            setBottlerOptions([]);

            fetchTargetTable([singleBottler.id]);
          } else {
            setUserBottler(null);
            setFilteredData([]);
            setSelectedBottlerOwnerCodes([]);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching bottler data:", error);
    }
  };

  const BOTTLER_WITH_ALL_OPTIONS = [
    { id: "ALL", label: "All", isSelectAll: true },
    ...bottlerOptions,
  ];

  const bottlerOnChangeHandler = (event, value) => {
    const hasSelectAll = value.some((item) => item.isSelectAll === true);

    let selectedBottlers = [];

    if (hasSelectAll) {
      const allSelected =
        bottlerList.length === bottlerOptions.length &&
        bottlerOptions.length > 0;

      selectedBottlers = allSelected ? [] : bottlerOptions;
      setBottlerList(selectedBottlers);
    } else {
      selectedBottlers = value;
      setBottlerList(value);
    }

    const bottlerOwnerCodes = selectedBottlers.map((item) => item.id);
    setSelectedBottlerOwnerCodes(bottlerOwnerCodes);

    if (bottlerOwnerCodes.length === 0) {
      setFilteredData([]);
      setLoading(false);
      return;
    }

    fetchTargetTable(bottlerOwnerCodes);
  };

  const fetchTargetTable = useCallback(async (bottlerOwnerCodes) => {
    setLoading(true);
    try {
      const response = await ApiService.post(
        "/v1/fg-inventory/targetDays/fetch",
        { bottlerOwnerCodes },
      );

      if (response.status === 200) {
        const processedData = (response.data || []).map((item) => ({
          ...item,
          isDataChanged: false,
        }));
        setFilteredData(processedData);
      }
    } catch (error) {
      console.error("Error fetching target table data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const columnDefinition = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        field: "supportCheckbox",
        headerName: "",
        sortable: false,
        width: 50,
        minWidth: 50,
        suppressSizeToFit: true,
      },
      {
        field: "bottlerOwnerCode",
        headerName: "Bottler Owner Code",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        hide: !isAdminOrSuperUser,
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 280,
        minWidth: 280,
        valueGetter: (params) => {
          const { bottlerOwnerCode } = params.data;
          return `${bottlerOwnerCode || ""}`;
        },
        cellRenderer: (params) => {
          const display = params.value;
          return (
            <Tooltip title={<span>{display}</span>} arrow>
              <div>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        field: "bottlerOwnerCode",
        headerName: "Bottler Owner Description",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        hide: !isAdminOrSuperUser,
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 280,
        minWidth: 280,
        valueGetter: (params) => {
          const { bottlerOwnerDescription } = params.data;
          return `${bottlerOwnerDescription || ""}`;
        },
        cellRenderer: (params) => {
          const display = params.value;
          return (
            <Tooltip title={<span>{display}</span>} arrow>
              <div>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Customer Country",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,

        valueGetter: (params) => {
          const countryCode = params.data?.countryCode ?? "";
          const countryName = params.data?.countryName ?? "";

          if (!countryCode && !countryName) return "";

          return `${countryCode} - ${countryName}`;
        },

        cellRenderer: (params) => {
          if (!params.value) return null;

          return (
            <Tooltip title={params.value} arrow>
              <div>{params.value}</div>
            </Tooltip>
          );
        },

        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            { filter: "agTextColumnFilter" },
            { filter: "agSetColumnFilter" },
          ],
        },
      },
      {
        headerName: "BPP Code",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,

        valueGetter: (params) => {
          const bppId = params.data?.bppCode ?? "";
          // const bppDesc = params.data?.bppDescription ?? "";

          if (!bppId) return "";

          return `${bppId}`;
        },

        cellRenderer: (params) => {
          if (!params.value) return null;

          return (
            <Tooltip title={params.value} arrow>
              <div>{params.value}</div>
            </Tooltip>
          );
        },

        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            { filter: "agTextColumnFilter" },
            { filter: "agSetColumnFilter" },
          ],
        },
      },
      {
        headerName: "BPP Description",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,

        valueGetter: (params) => {
          // const bppId = params.data?.bppCode ?? "";
          const bppDesc = params.data?.bppDescription ?? "";

          if (!bppDesc) return "";

          return `${bppDesc}`;
        },

        cellRenderer: (params) => {
          if (!params.value) return null;

          return (
            <Tooltip title={params.value} arrow>
              <div>{params.value}</div>
            </Tooltip>
          );
        },

        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            { filter: "agTextColumnFilter" },
            { filter: "agSetColumnFilter" },
          ],
        },
      },
      {
        headerName: "BPC Code",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,

        valueGetter: (params) => {
          const bppId = params.data?.bpc ?? "";

          if (!bppId) return "";

          return `${bppId}`;
        },

        cellRenderer: (params) => {
          if (!params.value) return null;

          return (
            <Tooltip title={params.value} arrow>
              <div>{params.value}</div>
            </Tooltip>
          );
        },

        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            { filter: "agTextColumnFilter" },
            { filter: "agSetColumnFilter" },
          ],
        },
      },
      {
        headerName: "BPC Description",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,

        valueGetter: (params) => {
          // const bppId = params.data?.bppCode ?? "";
          const bppDesc = params.data?.beverageProductDescription ?? "";

          if (!bppDesc) return "";

          return `${bppDesc}`;
        },

        cellRenderer: (params) => {
          if (!params.value) return null;

          return (
            <Tooltip title={params.value} arrow>
              <div>{params.value}</div>
            </Tooltip>
          );
        },

        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            { filter: "agTextColumnFilter" },
            { filter: "agSetColumnFilter" },
          ],
        },
      },
      {
        field: "targetDays",
        headerName: "Target Days of Supply",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,

        // Disabled for both INPROCESS and DELAYED
        editable: (params) => {
          const status = params.data?.status;
          return status !== "INPROCESS" && status !== "DELAYED";
        },

        cellClass: (params) => {
          const status = params.data?.status;
          const baseClass = "cpsComments-cell";
          return status === "INPROCESS" || status === "DELAYED"
            ? [baseClass, "ag-cell-disabled"]
            : baseClass;
        },

        cellEditor: "agTextCellEditor",
        cellEditorParams: {
          maxLength: 5,
        },

        valueSetter: (params) => {
          const status = params.data?.status;
          if (status === "INPROCESS" || status === "DELAYED") {
            return false;
          }

          const oldValue = params.data.targetDays;
          let newValue = params.newValue;

          if (newValue !== "" && !/^[0-9]+$/.test(newValue)) {
            return false;
          }

          params.data.targetDays = newValue;

          if (oldValue !== newValue) {
            params.data.isDataChanged = true;
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: ["rowSave"],
              force: true,
            });
          }
          return true;
        },

        cellRenderer: (params) => {
          const status = params.data?.status;
          const isDisabled = status === "INPROCESS" || status === "DELAYED";

          const tooltipText = isDisabled
            ? "Target Days cannot be edited - Status is IN PROCESS / DELAYED"
            : (params.value ?? "");

          return (
            <Tooltip title={tooltipText} arrow>
              <div
                style={{
                  color: isDisabled ? "#999" : "inherit",
                  cursor: isDisabled ? "not-allowed" : "default",
                }}
              >
                {params.value ?? ""}
              </div>
            </Tooltip>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 200,
        minWidth: 200,
        cellRenderer: (params) => (
          <Tooltip title={params.data.status || ""} arrow>
            <div>{params.data.status}</div>
          </Tooltip>
        ),
      },
      {
        headerName: "Save",
        field: "rowSave",
        width: 130,
        minWidth: 130,
        sortable: false,
        filter: false,
        suppressMenu: true,

        cellRenderer: (params) => {
          const hasPendingChanges = params.data?.isDataChanged === true;

          const onSaveRow = async () => {
            if (!hasPendingChanges) return;

            try {
              await saveTargetDays(params.data);

              // params.data.isDataChanged = false;

              params.api.refreshCells({
                rowNodes: [params.node],
                columns: ["rowSave"],
                force: true,
              });
            } catch (error) {
              console.error("Save failed", error);
            }
          };

          return (
            <IconButton
              size="small"
              disabled={!hasPendingChanges}
              onClick={onSaveRow}
              title={hasPendingChanges ? "Save changes" : "No changes to save"}
            >
              <SaveIcon color={hasPendingChanges ? "primary" : "disabled"} />
            </IconButton>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: "Upload By",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 200,
        minWidth: 200,
        cellRenderer: (params) => (
          <Tooltip title={params.data.updatedBy || ""} arrow>
            <div>{params.data.updatedBy}</div>
          </Tooltip>
        ),
      },
      {
        field: "updatedAt",
        headerName: "Upload Date",
        sortable: true,
        sort: "desc",
        filterParams: {
          comparator: function (filterDate, cellValue) {
            if (!cellValue) return -1;

            const cellDate = new Date(cellValue);
            const cellDateOnly = new Date(
              cellDate.getFullYear(),
              cellDate.getMonth(),
              cellDate.getDate(),
            );

            return cellDateOnly.getTime() - filterDate.getTime();
          },
          filterOptions: ["equals", "inRange"],
          defaultOption: "equals",
          buttons: ["apply", "reset"],
        },
        filter: "agDateColumnFilter",
        width: 210,
        minWidth: 210,
        valueFormatter: (params) => {
          return safeTimestamp(params.value);
        },
      },
    ],
    [fetchTargetTable, selectedBottlerOwnerCodes],
  );

  const saveTargetDays = async (rowData) => {
    try {
      const payload = [
        {
          bottler: rowData.bottler,
          bottlerOwnerCode: rowData.bottlerOwnerCode,
          bottlerOwnerDescription: rowData.bottlerOwnerDescription,
          countryCode: rowData.countryCode,
          countryName: rowData.countryName,
          bppCode: rowData.bppCode,
          bppDescription: rowData.bppDescription,
          targetDays: rowData.targetDays,
          updatedBy: rowData.updatedBy,
        },
      ];

      const response = await ApiService.post(
        "/v1/fg-inventory/targetDays/save",
        payload,
      );

      if (response.status === 200 || response.data?.status === "success") {
        MessagePopup({
          icon: "success",
          title: "Success",
          message: "Target Days saved successfully.",
        });

        if (selectedBottlerOwnerCodes.length > 0) {
          await fetchTargetTable([...selectedBottlerOwnerCodes]);
        }
      }
    } catch (error) {
      console.error("Save Error:", error);
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to save Target Days.",
      });
      throw error;
    }
  };

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();

      // Adding a resize event listener to handle zoom changes
      window.addEventListener("resize", () => {
        gridApi.sizeColumnsToFit();
        gridApi.refreshCells();
      });
    } else {
      console.error("Grid API is not available.");
    }
  };

  useEffect(() => {
    if (GridRef.current && GridRef.current.api && filteredData.length > 0) {
      GridRef.current.api.sizeColumnsToFit();
    }
  }, [filteredData]);

  const onCellValueChanged = (params) => {
    if (params.column.getColId() === "targetDays") {
      if (params.oldValue !== params.newValue) {
        params.data.isDataChanged = true;

        params.api.refreshCells({
          rowNodes: [params.node],
          columns: ["rowSave"],
          force: true,
        });
      }
    }
  };

  // const DownloadTemplate = async (e) => {
  //   e.preventDefault();

  //   let bottlerOwnerCodes = [];

  //   if (isAdminOrSuperUser) {
  //     bottlerOwnerCodes = bottlerList.map((item) => item.id);
  //   } else {
  //     if (userBottler?.id) {
  //       bottlerOwnerCodes = [userBottler.id];
  //     }
  //   }

  //   // If no bottler selected
  //   if (bottlerOwnerCodes.length === 0) {
  //     MessagePopup({
  //       icon: "error",
  //       title: "Error!",
  //       message: "Please select at least one Bottler Owner Code.",
  //     });
  //     return;
  //   }

  //   setDownloadLoading(true);

  //   const payloadData = {
  //     bottlerOwnerCodes: bottlerOwnerCodes,
  //   };

  //   try {
  //     const response = await ApiService.downloadFile(
  //       "/v1/fg-inventory/targetDays/generate-template",
  //       payloadData,
  //     );
  //     if (response.status === 200 && response.data) {
  //       // Create an object URL for the Blob
  //       const url = window.URL.createObjectURL(response.data);

  //       // Generate a timestamp for the filename (optional)
  //       const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "_");

  //       // Set the filename for the download
  //       const filename = `FG_TargetDays_${timestamp}1.xlsx`;

  //       // Create a temporary link to trigger the download
  //       const link = document.createElement("a");
  //       link.href = url;
  //       link.setAttribute("download", filename);

  //       // Append the link to the document body, click it, then remove it
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);

  //       // Clean up the object URL to release memory
  //       URL.revokeObjectURL(url);

  //       MessagePopup({
  //         icon: "success",
  //         message: "Excel file downloaded successfully!",
  //       });
  //     }
  //   } catch (error) {
  //     const errorMessage =
  //       // error.response?.data ||
  //       "Failed to Download file.";
  //     MessagePopup({
  //       icon: "error",
  //       title: "Error!",
  //       message: errorMessage,
  //     });
  //   } finally {
  //     setDownloadLoading(false);
  //   }
  // };

  const tableDownload = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");

    GridRef.current.api.exportDataAsExcel({
      fileName: `FG_TargetDays_of_Supply_${timestamp}.xlsx`,
      processCellCallback: ({ value }) => {
        if (value === null || value === undefined) return "";
        return `'${value.toString()}`;
      },
    });
  };

  const handleFileUpload = async (event) => {
    if (isUploading) return;

    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select a file!",
      });
      return;
    }

    let bottlerOwnerCodes = [];

    if (isAdminOrSuperUser) {
      bottlerOwnerCodes = bottlerList.map((item) => item.id);
    } else {
      if (userBottler?.id) {
        bottlerOwnerCodes = [userBottler.id];
      }
    }

    // If no bottler selected
    if (bottlerOwnerCodes.length === 0) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select at least one Bottler Owner Code.",
      });
      return;
    }

    const payloadData = {
      bottlerOwnerCodes: bottlerOwnerCodes,
    };
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("requestJson", JSON.stringify(payloadData));

    MessagePopup({
      title: "Confirm Submission of FG Inventory Target Days of Supply",
      html: `<p>Are you sure you want to submit the 
             <p>File selected: ${selectedFile.name} </p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsUploading(true);
        try {
          const response = await ApiService.uploadAndDownload(
            "/v1/fg-inventory/targetDays/upload",
            formData,
          );

          const validationResult = response.headers?.["x-validation-result"];

          if (validationResult === "SUCCESS") {
            MessagePopup({
              icon: "success",
              title: "Upload Completed",
              confirmButtonText: "Ok",
              confirmButtonColor: "#000000",
            });
          }
          MessagePopup({
            icon: "success",
            message: "file Upload successfully!",
          });

          // Refresh table
          if (selectedBottlerOwnerCodes.length > 0) {
            fetchTargetTable(selectedBottlerOwnerCodes);
          }
        } catch (error) {
          console.log(error);
          let text = "Error While Processing the upload";

          try {
            if (error.response?.data?.message) {
              text = error.response.data.message;
            } else if (error.response?.data instanceof Blob) {
              const blobText = await error.response.data.text();
              const parsed = JSON.parse(blobText);
              text = parsed.message || blobText;
            }
          } catch (e) {
            console.error("Error parsing error message:", e);
          }

          MessagePopup({
            icon: "error",
            title: "Upload Failed",
            html: `<p style="color: red; margin: 0;">${text}</p>`,
            confirmButtonText: "Ok",
            confirmButtonColor: "#000000",
          });
        } finally {
          setIsUploading(false);
        }
      } else {
        setIsUploading(false);
      }
    });
  };

  const handleRefresh = async () => {
    if (selectedBottlerOwnerCodes.length === 0) {
      MessagePopup({
        icon: "warning",
        title: "No Selection",
        message: "Please select at least one Bottler Owner Code to refresh.",
      });
      return;
    }

    setLoading(true);
    try {
      await fetchTargetTable(selectedBottlerOwnerCodes);
    } catch (error) {
      console.error("Refresh failed", error);
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to refresh data.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="container">
        <Grid xs={12}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            mb={1}
            mt={0.25}
          >
            {/* Left: Heading */}
            {/* <Grid item sx={{ display: "flex", alignItems: "center" }}> */}
            <Grid item xs={12} sm={6} md={4}>
              {isAdminOrSuperUser ? (
                <Autocomplete
                  multiple
                  limitTags={1}
                  disableCloseOnSelect
                  options={BOTTLER_WITH_ALL_OPTIONS}
                  value={bottlerList}
                  onChange={bottlerOnChangeHandler}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderOption={(props, option) => {
                    const { key, ...liProps } = props;
                    const isSelectAll = option?.isSelectAll === true;
                    const checked = isSelectAll
                      ? bottlerList.length === bottlerOptions.length &&
                      bottlerOptions.length > 0
                      : bottlerList.some((item) => item.id === option.id);

                    return (
                      <li key={key} {...liProps}>
                        <Checkbox sx={{ mr: 1 }} checked={checked} />
                        {option.label}
                      </li>
                    );
                  }}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip key={key} label={option.label} {...tagProps} />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bottler Owner Code*"
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                        style: {
                          transform: "translate(14px, -14px) scale(0.75)",
                        },
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  label="Bottler Owner Code"
                  size="small"
                  fullWidth
                  value={userBottler?.label || "No Bottler Assigned"}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{
                    shrink: true,
                    style: { transform: "translate(14px, -14px) scale(0.75)" },
                  }}
                />
              )}
            </Grid>
            {/* </Grid> */}

            {/* Right: Buttons */}
            <Grid item>
              <Stack direction="row" spacing={1}>
                <ButtonComponent
                  maxWidth="150px"
                  label={"Download"}
                  bgColor="black"
                  color="white"
                  onClick={tableDownload}
                  // disabled={downloadloading}
                  startIcon={<DownloadIcon />}
                />
                <ButtonComponent
                  label={isUploading ? "Uploading..." : "Upload"}
                  maxWidth="150px"
                  bgColor="black"
                  color="white"
                  disabled={isUploading}
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                      fileInputRef.current.click();
                    }
                  }}
                  startIcon={<FileUploadIcon />}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />
                <Tooltip title="Refresh Data" arrow placement="top">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={loading || selectedBottlerOwnerCodes.length === 0}
                    color="default"
                    sx={{
                      padding: "8px",
                      display: "flex",
                      alignItems: "center", // Main alignment
                      justifyContent: "center",
                      marginTop: "-2px",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={12}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height="80vh"
              animation="pulse"
              style={{ marginBottom: "16px", borderRadius: "8px" }}
            />
          ) : (
            <DataTable
              ref={GridRef}
              columns={columnDefinition}
              rows={filteredData || []}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              tableHeight={"78vh"}
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              suppressPaginationPanel={false}
              onGridReady={onGrid}
              onCellValueChanged={onCellValueChanged}
            />
          )}
        </Grid>
      </div>
    </>
  );
}

export default FGTargetDaysofSupply;
